import React from 'react';
import { CuttingTest, Attachment } from '../types';
import { generateUUID, processFile } from '../utils/helpers';

const FieldAttachments: React.FC<{
    attachments: Attachment[];
    onRemove: (id: string) => void;
}> = ({ attachments, onRemove }) => {
    if (!attachments || attachments.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-2 mt-2">
            {attachments.map(att => (
                <div key={att.id} className="relative group w-16 h-16 border border-slate-600 rounded overflow-hidden bg-slate-800">
                    {att.fileType.startsWith('image/') ? (
                        <img src={att.data || att.url} alt={att.fileName} className="w-full h-full object-cover" />
                    ) : (
                         <div className="w-full h-full flex items-center justify-center bg-slate-800 text-slate-500 text-xs text-center p-1 break-words leading-tight">
                            <div>
                                <i className="fa-regular fa-file mb-1"></i>
                                <div>{att.fileName.split('.').pop()}</div>
                            </div>
                        </div>
                    )}
                    <button onClick={() => onRemove(att.id)} className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity">
                        <i className="fa-solid fa-xmark text-sm"></i>
                    </button>
                    {att.uploading && (
                         <div className="absolute inset-0 bg-slate-900/70 flex items-center justify-center">
                            <i className="fa-solid fa-circle-notch fa-spin text-red-500 text-xs"></i>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export const CuttingTests: React.FC<{
    tests: CuttingTest[];
    onUpdate: (tests: CuttingTest[]) => void;
    reportAttachments: Attachment[];
    onAddAttachment: (att: Attachment) => void;
    onRemoveAttachment: (id: string) => void;
}> = ({ tests, onUpdate, reportAttachments, onAddAttachment, onRemoveAttachment }) => {

    const handleAddTest = () => {
        const newTest: CuttingTest = {
            id: generateUUID(),
            parameterNotes: '',
            resultNotes: '',
            isFinal: false
        };
        onUpdate([...tests, newTest]);
    };

    const handleRemoveTest = (id: string) => {
        onUpdate(tests.filter(t => t.id !== id));
    };

    const handleUpdateTest = (id: string, field: keyof CuttingTest, value: any) => {
        onUpdate(tests.map(t => {
            if (t.id !== id) return t;
            return { ...t, [field]: value };
        }));
        
        if (field === 'isFinal' && value === true) {
             // Exclusive final: unset others
             onUpdate(tests.map(t => ({
                 ...t, 
                 isFinal: t.id === id ? true : false,
                 [field]: t.id === id ? value : t[field] // ensure we don't clobber the target update
             })));
        }
    };

    const handleFileUpload = async (files: FileList | null, testId: string, field: 'parameterPhoto' | 'resultPhoto') => {
        if (!files || files.length === 0) return;
        const file = files[0];
        
        const newAtt: Attachment = {
            id: generateUUID(),
            fileName: file.name,
            fileType: file.type,
            size: file.size,
            url: URL.createObjectURL(file),
            bucket: 'issue_photos',
            fieldRef: `${testId}-${field}`,
            uploaded: false,
            uploading: true
        };
        
        onAddAttachment(newAtt);
        
        try {
            const { data, type } = await processFile(file);
            const completedAtt = { ...newAtt, data, fileType: type, uploading: false, uploaded: true };
            onRemoveAttachment(newAtt.id);
            onAddAttachment(completedAtt);
        } catch (e) {
            console.error(e);
            onRemoveAttachment(newAtt.id);
        }
    };

    return (
        <div className="space-y-6">
            {tests.map((test, idx) => (
                <div key={test.id} className={`bg-slate-800 border rounded-lg p-4 transition-all ${test.isFinal ? 'border-green-500 shadow-md shadow-green-900/20' : 'border-slate-700'}`}>
                    <div className="flex justify-between items-center mb-4 pb-2 border-b border-slate-700">
                        <h5 className="font-bold text-slate-200">Test #{idx + 1}</h5>
                        <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    checked={test.isFinal} 
                                    onChange={e => handleUpdateTest(test.id, 'isFinal', e.target.checked)}
                                    className="rounded bg-slate-700 border-slate-500 text-green-500 focus:ring-green-500"
                                />
                                <span className={`text-xs font-bold uppercase ${test.isFinal ? 'text-green-400' : 'text-slate-500'}`}>
                                    {test.isFinal ? 'Final / Customer Accepted' : 'Mark as Final'}
                                </span>
                            </label>
                            <button onClick={() => handleRemoveTest(test.id)} className="text-slate-500 hover:text-red-500">
                                <i className="fa-solid fa-trash"></i>
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Parameter */}
                        <div className="bg-slate-900/50 p-3 rounded border border-slate-700/50">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-xs font-bold text-slate-400 uppercase">Parameters</span>
                                <label className="text-xs bg-slate-700 hover:bg-slate-600 px-2 py-1 rounded cursor-pointer transition-colors flex items-center gap-1 text-slate-300">
                                    <i className="fa-solid fa-camera"></i> Add Photo
                                    <input type="file" hidden accept="image/*" onChange={e => handleFileUpload(e.target.files, test.id, 'parameterPhoto')} />
                                </label>
                            </div>
                            
                            <FieldAttachments attachments={reportAttachments.filter(a => a.fieldRef === `${test.id}-parameterPhoto`)} onRemove={onRemoveAttachment} />
                            
                            <textarea 
                                rows={3}
                                className="w-full bg-slate-800 border-slate-700 text-white text-xs rounded p-2 mt-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder:text-slate-600"
                                placeholder="Speed, focus, nozzle, gas pressure..."
                                value={test.parameterNotes}
                                onChange={e => handleUpdateTest(test.id, 'parameterNotes', e.target.value)}
                            />
                        </div>

                        {/* Result */}
                        <div className="bg-slate-900/50 p-3 rounded border border-slate-700/50">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-xs font-bold text-slate-400 uppercase">Produced Result</span>
                                <label className="text-xs bg-slate-700 hover:bg-slate-600 px-2 py-1 rounded cursor-pointer transition-colors flex items-center gap-1 text-slate-300">
                                    <i className="fa-solid fa-camera"></i> Add Photo
                                    <input type="file" hidden accept="image/*" onChange={e => handleFileUpload(e.target.files, test.id, 'resultPhoto')} />
                                </label>
                            </div>

                            <FieldAttachments attachments={reportAttachments.filter(a => a.fieldRef === `${test.id}-resultPhoto`)} onRemove={onRemoveAttachment} />

                            <textarea 
                                rows={3}
                                className="w-full bg-slate-800 border-slate-700 text-white text-xs rounded p-2 mt-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder:text-slate-600"
                                placeholder="Edge quality, dross, striations..."
                                value={test.resultNotes}
                                onChange={e => handleUpdateTest(test.id, 'resultNotes', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            ))}

            <button 
                onClick={handleAddTest}
                className="w-full py-3 border-2 border-dashed border-slate-600 text-slate-400 rounded-lg hover:border-green-500 hover:text-green-500 font-bold uppercase tracking-wide text-xs transition-colors"
            >
                <i className="fa-solid fa-plus mr-2"></i> Add Cutting Test
            </button>
        </div>
    );
};
